import React, { useRef, useState, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

import TextFieldLayout from "components/common/TextfieldLayout";
import { Alert, Box, Button, Stack } from "@mui/material";

// Customizing the default link format
const Link = Quill.import("formats/link");

class CustomLink extends Link {
  static blotName = "link";
  static tagName = "A";

  static create(value) {
    const node = super.create(value);
    const sanitized = this.sanitize(value);
    node.setAttribute("href", sanitized);
    node.setAttribute("target", "_blank");
    node.setAttribute("contenteditable", "false");
    return node;
  }
}

Quill.register(CustomLink, true);

export default React.memo(function Editor(props) {
  const { value, onChange, linkCheck = false } = props;
  const quillRef = useRef(null);
  const [showDocButton, setShowDocButton] = useState(false);

  // Check if document link is already present
  const checkForDocumentLink = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const html = quill.root.innerHTML;
      const hasDocLink = html.includes('<a href="{{DocumentLink}}"');
      setShowDocButton(!hasDocLink);
    }
  };

  const handleInsertDocumentLink = () => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();
    if (range) {
      const customHtml = `
        <p>
          <a href="{{DocumentLink}}" rel="noopener noreferrer" 
             style="color: rgb(255, 255, 255); background-color: rgb(13, 152, 186);">
            View &amp; Download Your Credential
          </a>
        </p>`;
      quill.clipboard.dangerouslyPasteHTML(range.index, customHtml);
      checkForDocumentLink(); // update visibility
    }
  };

  useEffect(() => {
    if (linkCheck) {
      checkForDocumentLink(); // run on mount and whenever value changes
    }
  }, [value]);

  return (
    <Stack spacing={2}>
      <TextFieldLayout {...props}>
        {showDocButton && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <Button
              sx={{ position: "absolute", right: 6, top: 6 }}
              variant="contained"
              onClick={handleInsertDocumentLink}
            >
              Add Document Link
            </Button>
          </Box>
        )}
        <Box
          sx={{
            "& .ql-toolbar.ql-snow": { borderWidth: "0 0 1px" },
            "& .ql-container.ql-snow": { border: 0 },
          }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={(content) => {
              onChange(content);
              if (linkCheck) {
                checkForDocumentLink();
              }
            }}
          />
        </Box>
      </TextFieldLayout>
      {showDocButton && (
        <Alert severity="error">
          You need to add a document link to the editor to enable recipients to
          view and download their credentials.
        </Alert>
      )}
    </Stack>
  );
});
